<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn
            id="gestaoDeAula.voltar"
            color="primary"
            x-small
            text
            @click="() => $router.push({ name: 'gestoesDeAulas' })"
            :disabled="submittingForm"
          >
            <v-icon left> fa fa-angle-left </v-icon>
            <span class="sr-only">Voltar</span>
          </v-btn>
          {{ editing ? "Editando" : "Criando" }} Vínculo
        </h1>
      </v-col>
    </v-row>
    <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(submitForm)" :disabled="submittingForm" class="pa-4">
        <v-row class="pl-2 rounded-lg">
          <v-col cols="12" md="6" lg="8">
            <ValidationProvider name="Professor" rules="required" v-slot="{ errors }">
              <e-label>Professor</e-label>
              <e-autocomplete
                id="form.instrutor_id"
                :items="professoresList"
                :return-object="false"
                :item-value="(value) => value.id"
                :item-text="(val) => `${val.cpf} - ${val.nome} - ${val.user.roles[0].name}`"
                :error-messages="errors"
                :loading="carregandoProfessores"
                v-model="form.instrutor_id"
                label="Selecione uma opção"
                dense
                solo
              />
            </ValidationProvider>
          </v-col>

          <v-col cols="12" md="6" lg="4">
            <ValidationProvider name="Tipo" rules="required" v-slot="{ errors }">
              <e-label>Tipo do Professor</e-label>
              <v-select
                id="form.tipo_professor"
                :items="tipos"
                :item-value="(value) => value.id"
                :item-text="(val) => val.descricao"
                label="Selecione uma opção"
                v-model="form.tipo_professor"
                :error-messages="form.errors ? form.errors : errors"
                dense
                solo
              ></v-select>
            </ValidationProvider>
          </v-col>
        </v-row>

        <v-row
          class="animate__animated animate__zoomIn mb-6 pa-2 rounded-lg elevation-1"
          style="animation-duration: 0.5s"
          v-for="relacao of relacoes"
          :key="relacoes.indexOf(relacao)"
        >
          <!-- Botão de excluir relação, no canto superior direito do bloco -->
          <v-col cols="12" class="d-flex justify-end pb-0" v-if="relacoes.length > 1">
            <v-btn
              id="relacao.trash"
              color="error"
              small
              icon
              @click="() => trashRelacao(relacoes, relacao)"
              :disabled="submittingForm"
            >
              <v-icon small>fa-trash</v-icon>
            </v-btn>
          </v-col>

          <!-- ===== Bloco superior: dados da turma/gestão (esquerda) + Componentes Curriculares (direita) ===== -->
          <v-col cols="12" lg="6">
            <v-row>
              <v-col cols="12" md="4">
                <ValidationProvider name="Turma" rules="required" v-slot="{ errors }">
                  <e-label>Turma</e-label>
                  <e-autocomplete
                    id="relacao.turma_id"
                    :items="turmasList"
                    :return-object="false"
                    :item-value="(value) => value.id"
                    :item-text="(val) => val.descricao"
                    :error-messages="relacao.errors ? relacao.errors : errors"
                    :loading="carregandoTurmas"
                    v-model="relacao.turma_id"
                    :disabled="editing"
                    @change="onTurmaChange(relacao)"
                    label="Selecione uma opção"
                    dense
                    solo
                  />
                </ValidationProvider>
              </v-col>

              <v-col cols="12" md="4">
                <ValidationProvider
                  name="Carga horária (horas)"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <e-label>Carga horária <small>(horas)</small></e-label>
                  <v-text-field
                    id="relacao.carga_horaria"
                    type="number"
                    v-model="relacao.carga_horaria"
                    dense
                    solo
                    readonly
                    :error-messages="errors"
                    min="0"
                  />
                </ValidationProvider>
              </v-col>

              <v-col v-if="editing" cols="12" md="4">
                <e-label>Tipo da Gestão</e-label>
                <br />
                <v-chip :color="relacao.is_polivalencia ? 'primary' : 'grey lighten-2'" label>
                  {{ relacao.is_polivalencia ? "Polivalente" : "Monovalente" }}
                </v-chip>
                <div v-if="relacao.is_polivalencia" class="text-caption grey--text mt-2">
                  Esta gestão possui mais de um componente curricular.
                </div>
              </v-col>
            </v-row>

            <!-- Qtd. de aulas por etapa: fica logo abaixo da Legenda, na mesma coluna esquerda,
                 para não depender da altura da lista de Componentes Curriculares à direita.
                 Sempre calculada automaticamente a partir da grade — não é mais editável. -->
            <v-row>
              <v-col cols="12">
                <v-card>
                  <v-card-title>
                    <e-label>Quantidade de Aulas por etapa</e-label>
                  </v-card-title>

                  <v-card-text v-if="relacao.turma_id">
                    <e-tabela-aulas-por-etapa
                      :matriz="relacao.grade.matriz_aulas_etapa"
                      :componentes-cores="relacao.grade.componentes"
                    />
                  </v-card-text>

                  <div v-else class="pa-4">
                    <p class="text-center">
                      <v-icon lg class="mr-2" color="primary">mdi mdi-alert</v-icon>
                      <span>Selecione uma turma primeiro!</span>
                    </p>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Vínculo de Dias e Horários: agora empilhado logo abaixo de
                 Quantidade de Aulas por Etapa, na mesma coluna esquerda. -->
            <v-row>
              <v-col cols="12">
                <v-card>
                  <v-card-title>
                    <e-label>Vínculo de Dias e Horários</e-label>
                  </v-card-title>
                  <v-card-text>
                    <div v-if="relacao.celulasGrade.length > 0">
                      <v-row class="mb-1" no-gutters>
                        <v-col cols="4" class="text-caption text-uppercase grey--text">Dia</v-col>
                        <v-col cols="4" class="text-caption text-uppercase grey--text"
                          >Horário</v-col
                        >
                        <v-col cols="4" class="text-caption text-uppercase grey--text"
                          >Componente</v-col
                        >
                      </v-row>
                      <v-row
                        v-for="(item, i) in detalhesGrade(relacao)"
                        :key="i"
                        no-gutters
                        class="py-1"
                        style="border-top: 1px solid rgba(0, 0, 0, 0.06)"
                      >
                        <v-col cols="4" class="text-body-2">{{ item.dia }}</v-col>
                        <v-col cols="4" class="text-body-2">{{ item.horario }}</v-col>
                        <v-col cols="4">
                          <v-chip x-small :color="item.cor" dark>
                            {{ item.componente }}
                          </v-chip>
                        </v-col>
                      </v-row>
                    </div>
                    <p v-else class="text-center">Não há horários selecionados</p>
                    <v-btn
                      id="relacao.dias-horarios"
                      color="green white--text"
                      class="mt-3"
                      :disabled="!relacao.turma_id"
                      @click="abrirDialogGrade(relacao)"
                      block
                    >
                      Dias e horários
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12" lg="6">
            <e-painel-componentes-curriculares
              v-if="relacao.turma_id"
              :value="relacao.componentesSelecionados"
              @input="(ids) => onComponentesInput(relacao, ids)"
              :componentes="relacao.componentesStatus"
              :carregando="relacao.carregandoComponentes"
            />
            <v-card v-else outlined class="fill-height d-flex align-center justify-center pa-4">
              <div class="text-center grey--text">
                <v-icon class="mr-2" color="primary">mdi mdi-alert</v-icon>
                Selecione uma turma para ver os componentes curriculares.
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" class="d-flex justify-space-between">
            <div class="d-flex">
              <v-btn
                id="gestaoDeAula.submit"
                color="primary"
                :disabled="submittingForm"
                :loading="submittingForm"
                type="submit"
                >Enviar Dados</v-btn
              >
              <v-btn
                id="gestaoDeAula.adicionar"
                class="ml-3"
                v-if="!editing"
                color="secondary"
                @click="() => handleSubmit(adicionarRelacao)"
                :disabled="submittingForm"
              >
                Adicionar
              </v-btn>
            </div>
          </v-col>
        </v-row>
        <v-dialog v-model="dialogGradeVisivel" max-width="1600" scrollable persistent>
          <v-card v-if="relacaoDialogGrade">
            <v-toolbar color="primary" dark>
              Vínculo de dias e horários
              <v-spacer></v-spacer>
              <v-btn icon @click="fecharDialogGrade" id="dias-horarios.fechar">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text class="pt-4">
              <e-grade-dias-horarios
                :value="celulasRascunho"
                @input="onCelulasRascunhoInput"
                :grade="gradeRascunho"
                :carregando="carregandoRascunho"
                :grade-legada-pendente="
                  relacaoDialogGrade ? relacaoDialogGrade.gradeLegadaPendente : false
                "
              />
            </v-card-text>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn id="dias-horarios.cancelar" text @click="fecharDialogGrade"> Cancelar </v-btn>
              <v-btn id="dias-horarios.salvar" color="primary" @click="salvarGrade">
                Aplicar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </form>
    </ValidationObserver>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import ELabel from "../../components/ELabel.vue";

const GRADE_VAZIA = () => ({
  horarios: [],
  dias: [],
  componentes: [],
  celulas: [],
  resumo: { total_horarios: 0, total_dias: 0, dias_descricoes: [] },
  grade_pendente_reconfiguracao: false,
  matriz_aulas_etapa: { etapas: [], componentes: [] },
});

export default {
  components: { ELabel },
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
  },
  mounted() {
    this.loadData();
  },
  data() {
    return {
      tipos: [],
      submittingForm: false,
      professoresList: [],
      turmasList: [],
      dialogGradeVisivel: false,
      relacaoDialogGrade: null,
      celulasRascunho: [],
      gradeRascunho: GRADE_VAZIA(),
      carregandoRascunho: false,
      rascunhoRequestId: 0,
      form: {},
      relacoes: [this.criarRelacaoVazia()],
      carregandoProfessores: false,
      carregandoTurmas: false,
      diasDaSemana: [
        { id: 0, descricao: "Segunda" },
        { id: 1, descricao: "Terça" },
        { id: 2, descricao: "Quarta" },
        { id: 3, descricao: "Quinta" },
        { id: 4, descricao: "Sexta" },
        { id: 5, descricao: "Sábado" },
      ],
    };
  },
  methods: {
    criarRelacaoVazia() {
      return {
        id: null,
        instrutor_id: null,
        tipo_professor: null,
        turma_id: null,
        is_polivalencia: 0,
        carga_horaria: 0,
        disciplinasDisponiveis: [],
        carregandoDisciplinas: false,
        componentesSelecionados: [],
        componentesStatus: [],
        carregandoComponentes: false,
        celulasGrade: [],
        grade: GRADE_VAZIA(),
        // Snapshot fixo do que a gestão tinha ao carregar a tela — só reflete dado legado
        // real (salvo antes desta feature existir), nunca é recalculado pelos previews do
        // rascunho local, senão uma célula que o usuário acabou de marcar (ainda sem
        // componente escolhido) apareceria com o mesmo aviso de dado legado quebrado.
        gradeLegadaPendente: false,
        carregandoGrade: false,
      };
    },

    trashRelacao(relacoes, relacao) {
      this.relacoes.splice(this.relacoes.indexOf(relacao), 1);
      this.$toast.success("Relação deletada com sucesso!");
    },

    async adicionarRelacao() {
      this.relacoes.push(this.criarRelacaoVazia());
    },

    abrirDialogGrade(relacao) {
      this.relacaoDialogGrade = relacao;
      this.celulasRascunho = relacao.celulasGrade.map((celula) => ({ ...celula }));
      this.gradeRascunho = { ...relacao.grade };
      this.dialogGradeVisivel = true;
    },

    fecharDialogGrade() {
      this.dialogGradeVisivel = false;
      this.relacaoDialogGrade = null;
      this.celulasRascunho = [];
      this.gradeRascunho = GRADE_VAZIA();
    },

    async onCelulasRascunhoInput(celulas) {
      this.celulasRascunho = celulas;
      const relacao = this.relacaoDialogGrade;
      if (!relacao) return;

      // Cliques rápidos em "+" em células diferentes disparam vários previews em paralelo;
      // sem essa checagem, uma resposta mais antiga podia chegar depois de uma mais nova e
      // sobrescrever o rascunho com um estado desatualizado (ex.: sumindo com um componente
      // recém-adicionado a outra célula).
      const requestId = ++this.rascunhoRequestId;

      this.carregandoRascunho = true;
      try {
        const grade =
          this.editing && relacao.id
            ? await this.$services.gestoesDeAulasService.previewGradeHorariosDaGestao(
                relacao.id,
                relacao.componentesSelecionados,
                celulas
              )
            : await this.$services.gestoesDeAulasService.previewGradeHorarios(
                relacao.turma_id,
                relacao.componentesSelecionados,
                celulas
              );
        if (requestId === this.rascunhoRequestId) {
          this.gradeRascunho = grade;
        }
      } catch (error) {
        this.$handleError(error);
      } finally {
        if (requestId === this.rascunhoRequestId) {
          this.carregandoRascunho = false;
        }
      }
    },

    salvarGrade() {
      // Sempre aplica no rascunho local da relação (criação e edição): nada é persistido
      // aqui, só ao clicar em "Enviar Dados" — mantém o mesmo comportamento nos dois modos.
      const relacao = this.relacaoDialogGrade;
      if (!relacao) return;

      if (this.celulasRascunho.some((celula) => !celula.disciplina_id)) {
        this.$toast.danger("Selecione o componente curricular de todos os horários marcados.");
        return;
      }

      this.$set(relacao, "celulasGrade", this.celulasRascunho);
      this.$set(relacao, "grade", this.gradeRascunho);
      this.calcularCargaHoraria(relacao);
      this.fecharDialogGrade();
    },

    async submitForm() {
      this.submittingForm = true;

      try {
        if (this.editing) {
          const relacao = this.relacoes[0];

          if (relacao.componentesSelecionados.length === 0) {
            this.$toast.danger("Selecione ao menos um componente curricular.");
            this.submittingForm = false;
            return;
          }

          if (relacao.celulasGrade.length === 0) {
            this.$toast.danger("Preencha os campos obrigatórios: Dias e horários.");
            this.submittingForm = false;
            return;
          }

          await this.$services.gestoesDeAulasService.atualizar({
            id: relacao.id,
            instrutor_id: this.form.instrutor_id,
            tipo_professor: this.form.tipo_professor,
            disciplina_ids: relacao.componentesSelecionados,
            celulas: relacao.celulasGrade,
            deleted_at: null,
          });

          this.$toast.success("Vínculo atualizado com sucesso!");
          this.$router.push({ name: "gestoesDeAulas" });
          this.submittingForm = false;
          return;
        }

        if (this.relacoes.some((relacao) => relacao.componentesSelecionados.length === 0)) {
          this.$toast.danger("Selecione ao menos um componente curricular em cada relação.");
          this.submittingForm = false;
          return;
        }

        if (this.relacoes.some((relacao) => relacao.celulasGrade.length === 0)) {
          this.$toast.danger("Preencha os campos obrigatórios: Dias e horários.");
          this.submittingForm = false;
          return;
        }

        const formRelacoes = this.relacoes.map((relacao) => {
          const isPolivalencia = relacao.componentesSelecionados.length > 1;

          return {
            instrutor_id: this.form.instrutor_id,
            tipo_professor: this.form.tipo_professor,
            turma_id: relacao.turma_id,
            carga_horaria: relacao.carga_horaria,
            is_polivalencia: isPolivalencia ? 1 : 0,
            disciplina_id: isPolivalencia ? null : relacao.componentesSelecionados[0] ?? null,
            disciplinas_polivalencia: relacao.componentesSelecionados,
            relacoesDiasHorarios: relacao.celulasGrade.map((celula) =>
              this.celulaParaRelacao(relacao, celula)
            ),
          };
        });

        const responseData = await this.$services.gestoesDeAulasService.criar(formRelacoes);
        const comErro = responseData.filter((item) => item.errors);

        comErro.forEach((item) => {
          (item.errors || []).forEach((mensagem) => this.$toast.danger(mensagem));
        });

        if (comErro.length > 0) {
          this.submittingForm = false;
          return;
        }

        this.$toast.success("Vínculo(s) criado(s) com sucesso!");
        this.$router.push({ name: "gestoesDeAulas" });
      } catch (error) {
        // No modo edição, um 422 aqui é o backend recusando o envio porque algo mudou
        // entre carregar a tela e clicar em "Enviar Dados" (ex.: outra gestão ocupou um
        // componente ou horário que estava disponível) — mostra exatamente o que mudou.
        const mensagens = this.mensagensDeErro(error);
        if (mensagens) {
          mensagens.forEach((mensagem) => this.$toast.danger(mensagem));
        } else {
          this.$handleError(error);
        }
      }
      this.submittingForm = false;
    },

    detalhesGrade(relacao) {
      const dias = new Map(this.diasDaSemana.map((d) => [d.id, d.descricao]));
      const horarios = new Map((relacao.grade.horarios || []).map((h) => [h.id, h.descricao]));
      const componentes = new Map((relacao.grade.componentes || []).map((c) => [c.id, c]));

      return relacao.celulasGrade
        .map((celula) => {
          const componente = componentes.get(celula.disciplina_id);
          return {
            diaId: celula.dia_id,
            horarioId: celula.horario_id,
            dia: dias.get(celula.dia_id) || `Dia ${celula.dia_id}`,
            horario: horarios.get(celula.horario_id) || `Horário ${celula.horario_id}`,
            componente: componente ? componente.descricao : "—",
            cor: componente ? componente.cor : "grey",
          };
        })
        .sort((a, b) => a.diaId - b.diaId || a.horarioId - b.horarioId);
    },

    celulaParaRelacao(relacao, celula) {
      const dia = this.diasDaSemana.find((d) => d.id === celula.dia_id);
      const horario = (relacao.grade.horarios || []).find((h) => h.id === celula.horario_id);
      const componente = (relacao.grade.componentes || []).find(
        (c) => c.id === celula.disciplina_id
      );

      return {
        dia: { id: celula.dia_id, descricao: dia ? dia.descricao : null },
        horario: { id: celula.horario_id, descricao: horario ? horario.descricao : null },
        disciplina: {
          id: celula.disciplina_id,
          descricao: componente ? componente.descricao : null,
        },
      };
    },

    async loadData() {
      try {
        this.$loaderService.open("Carregando dados");
        this.professoresList = await this.$services.professoresService.syncAll();
        this.tipos = await this.$services.professoresService.tipos();
        this.turmasList = await this.$services.turmasService.syncAll();

        if (this.editing) {
          const vinculo = await this.$services.gestoesDeAulasService.gestaoGeralId(
            parseInt(this.$route.params.gestaoDeAulasId, 10)
          );

          this.form.instrutor_id = vinculo.instrutor_id;
          this.form.tipo_professor = vinculo.tipo_professor;

          const relacao = this.criarRelacaoVazia();
          relacao.id = vinculo.id;
          relacao.turma_id = vinculo.turma_id;
          relacao.is_polivalencia = vinculo.is_polivalencia;

          this.relacoes = [relacao];

          await this.getDisciplinasDaTurma(relacao);
          await this.atualizarComponentesEdicao(relacao);
          await this.atualizarGradeEdicao(relacao);
          this.calcularCargaHoraria(relacao);
        }
        this.$loaderService.close();
      } catch (error) {
        this.$handleError(error);
        this.$loaderService.close();
      }
    },

    async onTurmaChange(relacao) {
      relacao.componentesSelecionados = [];
      relacao.celulasGrade = [];

      const requestId = this.proximoPreviewRequestId(relacao);
      this.getDisciplinasDaTurma(relacao);
      await this.atualizarPreviewComponentesCriacao(relacao, requestId);
      await this.atualizarPreviewGradeCriacao(relacao, requestId);
    },

    async getDisciplinasDaTurma(relacao) {
      if (!relacao.turma_id) {
        this.$set(relacao, "disciplinasDisponiveis", []);
        return;
      }

      this.$set(relacao, "carregandoDisciplinas", true);
      try {
        const disciplinas = await this.$services.turmasService.getDisciplinas(relacao.turma_id);
        this.$set(relacao, "disciplinasDisponiveis", disciplinas);
      } catch (error) {
        this.$toast.danger("Erro ao carregar as disciplinas da turma.");
        this.$set(relacao, "disciplinasDisponiveis", []);
      } finally {
        this.$set(relacao, "carregandoDisciplinas", false);
      }
    },

    onPolivalenciaChange(relacao) {
      if (relacao.is_polivalencia) {
        relacao.disciplina_id = null;
      } else {
        relacao.disciplinas_polivalencia = [];
      }
      this.calcularCargaHoraria(relacao);
      this.$nextTick(() => {
        if (this.$refs.observer) this.$refs.observer.reset();
      });
    },

    calcularCargaHoraria(relacao) {
      let cargaTotal = 0;
      const disciplinasDisponiveis = relacao.disciplinasDisponiveis || [];

      relacao.componentesSelecionados.forEach((disciplinaId) => {
        const disciplina = disciplinasDisponiveis.find((d) => d.id === disciplinaId);
        if (disciplina && disciplina.pivot && disciplina.pivot.carga_horaria_disciplina) {
          cargaTotal += parseFloat(disciplina.pivot.carga_horaria_disciplina);
        }
      });

      this.$set(relacao, "carga_horaria", cargaTotal);
    },

    mensagensDeErro(error) {
      // O interceptor global do Axios (src/plugins/Axios.js) já rejeita a promise com
      // error.response.data diretamente (não com o objeto de erro do Axios inteiro).
      return error && error.errors;
    },

    // ---- Fluxo de CRIAÇÃO: nada é persistido — cada interação só recalcula um preview
    // (estados/bloqueios) a partir de turma_id + seleção local do usuário. ----

    onComponentesInput(relacao, ids) {
      if (this.editing) {
        this.onComponentesInputEdicao(relacao, ids);
      } else {
        this.onComponentesInputCriacao(relacao, ids);
      }
    },

    // Marca qual é a chamada de preview mais recente para esta relação — usado para
    // descartar respostas que cheguem fora de ordem (ver comentário em
    // atualizarPreviewComponentesCriacao). Não precisa ser reativo, é só controle interno.
    proximoPreviewRequestId(relacao) {
      relacao.previewRequestId = (relacao.previewRequestId || 0) + 1;
      return relacao.previewRequestId;
    },

    async onComponentesInputCriacao(relacao, ids) {
      this.$set(relacao, "componentesSelecionados", ids);
      this.$set(relacao, "is_polivalencia", ids.length >= 2 ? 1 : 0);

      const idsSet = new Set(ids);
      this.$set(
        relacao,
        "celulasGrade",
        relacao.celulasGrade.filter((celula) => idsSet.has(celula.disciplina_id))
      );

      const requestId = this.proximoPreviewRequestId(relacao);
      await this.atualizarPreviewComponentesCriacao(relacao, requestId);
      await this.atualizarPreviewGradeCriacao(relacao, requestId);
      this.calcularCargaHoraria(relacao);
    },

    // Ao marcar 2 componentes em sequência rápida (comum na criação, onde nada começa
    // marcado), dois previews ficam em voo ao mesmo tempo. Sem o requestId, a resposta do
    // PRIMEIRO clique podia chegar depois da resposta do SEGUNDO e sobrescrever
    // relacao.grade/componentesStatus com um estado antigo (só 1 componente) — fazendo o
    // botão "+" da grade sumir para o componente que acabou de ser marcado.
    async atualizarPreviewComponentesCriacao(
      relacao,
      requestId = this.proximoPreviewRequestId(relacao)
    ) {
      if (!relacao.turma_id) return;
      this.$set(relacao, "carregandoComponentes", true);
      try {
        const status = await this.$services.gestoesDeAulasService.previewComponentesCurriculares(
          relacao.turma_id,
          relacao.componentesSelecionados
        );
        if (requestId === relacao.previewRequestId) {
          this.$set(relacao, "componentesStatus", status);
        }
      } catch (error) {
        this.$handleError(error);
      } finally {
        if (requestId === relacao.previewRequestId) {
          this.$set(relacao, "carregandoComponentes", false);
        }
      }
    },

    async atualizarPreviewGradeCriacao(relacao, requestId = this.proximoPreviewRequestId(relacao)) {
      if (!relacao.turma_id) return;
      this.$set(relacao, "carregandoGrade", true);
      try {
        const grade = await this.$services.gestoesDeAulasService.previewGradeHorarios(
          relacao.turma_id,
          relacao.componentesSelecionados,
          relacao.celulasGrade
        );
        if (requestId === relacao.previewRequestId) {
          this.$set(relacao, "grade", grade);
        }
      } catch (error) {
        this.$handleError(error);
      } finally {
        if (requestId === relacao.previewRequestId) {
          this.$set(relacao, "carregandoGrade", false);
        }
      }
    },

    // ---- Fluxo de EDIÇÃO: assim como na criação, nada é persistido aqui — cada interação
    // só recalcula um preview (estados/bloqueios), excluindo a própria gestão da checagem de
    // bloqueio. Tudo só é persistido de fato ao clicar em "Enviar Dados". ----

    async atualizarComponentesEdicao(relacao) {
      this.$set(relacao, "carregandoComponentes", true);
      try {
        const status = await this.$services.gestoesDeAulasService.componentesCurriculares(
          relacao.id
        );
        this.$set(relacao, "componentesStatus", status);
        this.$set(
          relacao,
          "componentesSelecionados",
          status.filter((c) => c.vinculado).map((c) => c.id)
        );
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.$set(relacao, "carregandoComponentes", false);
      }
    },

    async onComponentesInputEdicao(relacao, ids) {
      this.$set(relacao, "componentesSelecionados", ids);
      this.$set(relacao, "is_polivalencia", ids.length >= 2 ? 1 : 0);

      const idsSet = new Set(ids);
      this.$set(
        relacao,
        "celulasGrade",
        relacao.celulasGrade.filter((celula) => idsSet.has(celula.disciplina_id))
      );

      const requestId = this.proximoPreviewRequestId(relacao);
      await this.atualizarPreviewComponentesEdicao(relacao, requestId);
      await this.atualizarPreviewGradeEdicao(relacao, requestId);
      this.calcularCargaHoraria(relacao);
    },

    async atualizarPreviewComponentesEdicao(
      relacao,
      requestId = this.proximoPreviewRequestId(relacao)
    ) {
      this.$set(relacao, "carregandoComponentes", true);
      try {
        const status =
          await this.$services.gestoesDeAulasService.previewComponentesCurricularesDaGestao(
            relacao.id,
            relacao.componentesSelecionados
          );
        if (requestId === relacao.previewRequestId) {
          this.$set(relacao, "componentesStatus", status);
        }
      } catch (error) {
        this.$handleError(error);
      } finally {
        if (requestId === relacao.previewRequestId) {
          this.$set(relacao, "carregandoComponentes", false);
        }
      }
    },

    async atualizarPreviewGradeEdicao(relacao, requestId = this.proximoPreviewRequestId(relacao)) {
      this.$set(relacao, "carregandoGrade", true);
      try {
        const grade = await this.$services.gestoesDeAulasService.previewGradeHorariosDaGestao(
          relacao.id,
          relacao.componentesSelecionados,
          relacao.celulasGrade
        );
        if (requestId === relacao.previewRequestId) {
          this.$set(relacao, "grade", grade);
        }
      } catch (error) {
        this.$handleError(error);
      } finally {
        if (requestId === relacao.previewRequestId) {
          this.$set(relacao, "carregandoGrade", false);
        }
      }
    },

    async atualizarGradeEdicao(relacao) {
      this.$set(relacao, "carregandoGrade", true);
      try {
        const grade = await this.$services.gestoesDeAulasService.gradeHorarios(relacao.id);
        this.$set(relacao, "grade", grade);
        this.$set(relacao, "gradeLegadaPendente", grade.grade_pendente_reconfiguracao);
        this.$set(
          relacao,
          "celulasGrade",
          grade.celulas
            .filter((c) => c.selecionada)
            .flatMap((c) =>
              c.disciplinas && c.disciplinas.length
                ? c.disciplinas.map((d) => ({
                    dia_id: c.dia_id,
                    horario_id: c.horario_id,
                    disciplina_id: d.id,
                  }))
                : [{ dia_id: c.dia_id, horario_id: c.horario_id, disciplina_id: null }]
            )
        );
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.$set(relacao, "carregandoGrade", false);
      }
    },
  },
};
</script>

<style></style>
